import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  IconButton,
  Fade,
  alpha,
  useTheme,
  Skeleton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
  TableSortLabel,
  LinearProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, { getQuadrimestreAtual, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import ClassificacaoBadge from '../../components/saude/ClassificacaoBadge';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { buildRankingContext } from '../../utils/saude-context-text';

const EIXO_LABELS: Record<string, string> = {
  'c1-mais-acesso': 'C1',
  'c2-criancas': 'C2',
  'c3-gestantes': 'C3',
  'c4-diabeticos': 'C4',
  'c5-hipertensos': 'C5',
  'c6-idosos': 'C6',
  'c7-mulheres': 'C7',
};

const EIXO_NAMES: Record<string, string> = {
  'c1-mais-acesso': 'Mais Acesso',
  'c2-criancas': 'Saude da Crianca',
  'c3-gestantes': 'Saude da Gestante',
  'c4-diabeticos': 'Diabetes',
  'c5-hipertensos': 'Hipertensao',
  'c6-idosos': 'Saude do Idoso',
  'c7-mulheres': 'Saude da Mulher',
};

const ESF_EIXOS = ['c1-mais-acesso', 'c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c6-idosos', 'c7-mulheres'];

function getScoreColor(score: number): string {
  if (score >= 75) return '#2e7d32';
  if (score >= 50) return '#1565c0';
  if (score >= 25) return '#e65100';
  return '#c62828';
}

function getClassificacao(score: number): string {
  if (score >= 75) return 'Otimo';
  if (score >= 50) return 'Bom';
  if (score >= 25) return 'Suficiente';
  return 'Regular';
}

interface EquipeRanking {
  ine: string;
  equipe: string;
  unidade: string;
  score_consolidado: number;
  classificacao: string;
  eixos: Record<string, number>;
  eixos_regular: number;
  loadedEixos: number;
}

const SaudeRanking: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  // Per-eixo progressive state
  const [eixosEquipeData, setEixosEquipeData] = useState<Record<string, any[]>>({});
  const [eixosLoading, setEixosLoading] = useState<Set<string>>(new Set());
  const [eixosError, setEixosError] = useState<Set<string>>(new Set());

  const [searchTerm, setSearchTerm] = useState('');
  const [sortAsc, setSortAsc] = useState(true);

  const qAtual = getQuadrimestreAtual();
  const [quadrimestre, setQuadrimestre] = useState(qAtual.quadrimestre);
  const [ano, setAno] = useState(qAtual.ano);

  // Track current fetch cycle to ignore stale results
  const fetchCycleRef = useRef(0);

  const handleQuadChange = (q: string, a: number) => { setQuadrimestre(q); setAno(a); };

  const loadedCount = ESF_EIXOS.length - eixosLoading.size;
  const totalEixos = ESF_EIXOS.length;
  const isFullyLoaded = eixosLoading.size === 0 && loadedCount > 0;
  const isLoading = eixosLoading.size > 0;
  const hasAnyData = Object.keys(eixosEquipeData).length > 0;

  // Progressive fetch: one request per eixo, results arrive independently
  useEffect(() => {
    if (!municipioAtivo) return;

    const cycle = ++fetchCycleRef.current;
    const qParam = formatQuadrimestreParam(quadrimestre, ano);

    setEixosEquipeData({});
    setEixosLoading(new Set(ESF_EIXOS));
    setEixosError(new Set());

    for (const eixo of ESF_EIXOS) {
      saudeService.getEixoEquipes(eixo, municipioAtivo.id, qParam, unidadeSelecionada?.codunidade)
        .then((data: any) => {
          if (fetchCycleRef.current !== cycle) return;
          const rows = Array.isArray(data) ? data : (data?.rows || []);
          setEixosEquipeData(prev => ({ ...prev, [eixo]: rows }));
        })
        .catch(() => {
          if (fetchCycleRef.current !== cycle) return;
          setEixosError(prev => new Set(prev).add(eixo));
        })
        .finally(() => {
          if (fetchCycleRef.current !== cycle) return;
          setEixosLoading(prev => { const n = new Set(prev); n.delete(eixo); return n; });
        });
    }
  }, [municipioAtivo, quadrimestre, ano, unidadeSelecionada?.codunidade]);

  // Client-side aggregation: same logic as backend getRankingEquipes
  const equipes = useMemo<EquipeRanking[]>(() => {
    const equipeMap = new Map<string, { ine: string; equipe: string; unidade: string; scores: number[]; eixos: Record<string, number>; loadedEixos: number }>();

    for (const eixo of ESF_EIXOS) {
      const rows = eixosEquipeData[eixo];
      if (!rows) continue;
      for (const eq of rows) {
        if (!eq.ine) continue;
        if (!equipeMap.has(eq.ine)) {
          equipeMap.set(eq.ine, { ine: eq.ine, equipe: eq.equipe || '', unidade: eq.unidade || '', scores: [], eixos: {}, loadedEixos: 0 });
        }
        const entry = equipeMap.get(eq.ine)!;
        const score = typeof eq.score === 'number' ? eq.score : 0;
        entry.eixos[eixo] = score;
        entry.scores.push(score);
        entry.loadedEixos++;
        if (eq.equipe && !entry.equipe) entry.equipe = eq.equipe;
        if (eq.unidade && !entry.unidade) entry.unidade = eq.unidade;
      }
    }

    return Array.from(equipeMap.values()).map(eq => {
      const avgScore = eq.scores.length > 0
        ? Math.round(eq.scores.reduce((a, b) => a + b, 0) / eq.scores.length)
        : 0;
      const eixosRegular = eq.scores.filter(s => s < 25).length;
      return {
        ine: eq.ine,
        equipe: eq.equipe,
        unidade: eq.unidade,
        score_consolidado: avgScore,
        classificacao: getClassificacao(avgScore),
        eixos: eq.eixos,
        eixos_regular: eixosRegular,
        loadedEixos: eq.loadedEixos,
      };
    });
  }, [eixosEquipeData]);

  // Filtered and sorted
  const sortedEquipes = useMemo(() => {
    let filtered = [...equipes];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        eq => eq.equipe.toLowerCase().includes(term) || eq.unidade.toLowerCase().includes(term) || eq.ine.includes(term)
      );
    }
    filtered.sort((a, b) => sortAsc ? a.score_consolidado - b.score_consolidado : b.score_consolidado - a.score_consolidado);
    return filtered;
  }, [equipes, searchTerm, sortAsc]);

  // Context for IA - only when fully loaded
  const dadosContexto = useMemo(() => {
    if (!equipes.length || !isFullyLoaded) return undefined;
    const distribuicao = { Otimo: 0, Bom: 0, Suficiente: 0, Regular: 0 } as Record<string, number>;
    equipes.forEach(eq => { distribuicao[eq.classificacao] = (distribuicao[eq.classificacao] || 0) + 1; });
    return buildRankingContext({
      equipes: equipes.map(eq => ({
        equipe: eq.equipe,
        ine: eq.ine,
        unidade: eq.unidade,
        scoreConsolidado: eq.score_consolidado,
        classificacao: eq.classificacao,
        eixos: Object.fromEntries(Object.entries(eq.eixos).map(([k, v]) => [k, { score: v, classificacao: v >= 75 ? 'Otimo' : v >= 50 ? 'Bom' : v >= 25 ? 'Suficiente' : 'Regular' }])),
      })),
      distribuicao,
    });
  }, [equipes, isFullyLoaded]);

  // Summary counts
  const summary = useMemo(() => {
    const counts = { total: equipes.length, otimo: 0, bom: 0, regular: 0 };
    equipes.forEach(eq => {
      if (eq.classificacao === 'Otimo') counts.otimo++;
      else if (eq.classificacao === 'Bom') counts.bom++;
      else counts.regular++;
    });
    return counts;
  }, [equipes]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary">Selecione um municipio na barra lateral para visualizar o ranking.</Typography>
      </Box>
    );
  }

  const progressPct = totalEixos > 0 ? (loadedCount / totalEixos) * 100 : 0;
  const showInitialSkeleton = !hasAnyData && isLoading;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => navigate('/saude')}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700}>Ranking de Equipes</Typography>
            <Typography variant="body2" color="text.secondary">
              {municipioAtivo.nome} - eSF/eAP (C1-C7)
              {isLoading ? (' \u2014 Carregando ' + loadedCount + '/' + totalEixos + ' eixos...') : ''}
            </Typography>
          </Box>
          <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
        </Box>
      </Fade>

      {/* Progress bar */}
      {isLoading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={progressPct} sx={{ borderRadius: 2, height: 6 }} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center' }}>
            {ESF_EIXOS.map(eixo => {
              const loaded = eixo in eixosEquipeData;
              const errored = eixosError.has(eixo);
              return (
                <Tooltip key={eixo} title={EIXO_LABELS[eixo] + ' ' + EIXO_NAMES[eixo] + (errored ? ' - Erro' : loaded ? ' - OK' : ' - Carregando...')} arrow>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      bgcolor: errored ? alpha('#c62828', 0.2) : loaded ? alpha('#2e7d32', 0.15) : alpha('#9e9e9e', 0.15),
                      color: errored ? '#c62828' : loaded ? '#2e7d32' : '#9e9e9e',
                      transition: 'all 0.3s',
                    }}
                  >
                    {EIXO_LABELS[eixo]}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {eixosError.size > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
          {eixosError.size === totalEixos
            ? 'Erro ao carregar todos os eixos. Verifique a conexao e-SUS.'
            : 'Erro em ' + eixosError.size + ' eixo(s): ' + Array.from(eixosError).map(e => EIXO_LABELS[e]).join(', ')}
        </Alert>
      )}

      {/* Summary cards */}
      <Fade in timeout={700}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid ' + theme.palette.divider }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GroupsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Equipes</Typography>
                  {showInitialSkeleton ? <Skeleton width={40} height={32} /> : (
                    <Typography variant="h5" fontWeight={700}>{summary.total}</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid ' + theme.palette.divider }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha('#2e7d32', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmojiEventsIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Otimo</Typography>
                  {showInitialSkeleton ? <Skeleton width={40} height={32} /> : (
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>{summary.otimo}</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid ' + theme.palette.divider }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha('#1565c0', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmojiEventsIcon sx={{ color: '#1565c0', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Bom</Typography>
                  {showInitialSkeleton ? <Skeleton width={40} height={32} /> : (
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#1565c0' }}>{summary.bom}</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid ' + theme.palette.divider }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha('#c62828', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <WarningAmberIcon sx={{ color: '#c62828', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Regular/Suficiente</Typography>
                  {showInitialSkeleton ? <Skeleton width={40} height={32} /> : (
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#c62828' }}>{summary.regular}</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Search bar */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar equipe ou unidade..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {hasAnyData && (
            <Typography variant="body2" color="text.secondary">
              {sortedEquipes.length} equipes{isLoading ? (' (' + loadedCount + '/' + totalEixos + ' eixos)') : ''}
            </Typography>
          )}
        </Box>
      </Fade>

      {/* Table */}
      <Fade in timeout={900}>
        {showInitialSkeleton ? (
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid ' + theme.palette.divider, position: 'relative' }}>
            {isLoading && (
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, borderRadius: '12px 12px 0 0', height: 3 }}
              />
            )}
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell sx={{ fontWeight: 700, width: 48 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Equipe</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Unidade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    <TableSortLabel
                      active
                      direction={sortAsc ? 'asc' : 'desc'}
                      onClick={() => setSortAsc(!sortAsc)}
                    >
                      Score
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Classificacao</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }} align="center">Eixos (C1-C7)</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }} align="center">Alertas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEquipes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'Nenhuma equipe encontrada com esse filtro.' : 'Nenhuma equipe disponivel.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedEquipes.map((eq, idx) => {
                    const scoreColor = getScoreColor(eq.score_consolidado);
                    return (
                      <TableRow
                        key={eq.ine}
                        hover
                        sx={{ cursor: 'default', '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">{idx + 1}</Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{eq.equipe}</Typography>
                          <Typography variant="caption" color="text.secondary">{eq.ine}</Typography>
                        </TableCell>

                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                            {eq.unidade}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ color: scoreColor }}>
                            {eq.score_consolidado}
                          </Typography>
                          {isLoading && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                              ({eq.loadedEixos}/{totalEixos})
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <ClassificacaoBadge classificacao={eq.classificacao} size="small" />
                        </TableCell>

                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            {ESF_EIXOS.map(eixo => {
                              const eixoLoaded = eixo in eixosEquipeData;
                              const score = eq.eixos[eixo];

                              if (!eixoLoaded) {
                                return (
                                  <Tooltip key={eixo} title={EIXO_LABELS[eixo] + ' ' + EIXO_NAMES[eixo] + ' - Carregando...'} arrow>
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor: alpha('#9e9e9e', 0.15),
                                        flexShrink: 0,
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                        '@keyframes pulse': {
                                          '0%, 100%': { opacity: 0.4 },
                                          '50%': { opacity: 1 },
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                );
                              }

                              if (score === undefined || score === null) {
                                return (
                                  <Tooltip key={eixo} title={EIXO_LABELS[eixo] + ' ' + EIXO_NAMES[eixo] + ' - Sem dados'} arrow>
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor: alpha('#9e9e9e', 0.3),
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              }
                              const chipColor = getScoreColor(score);
                              return (
                                <Tooltip key={eixo} title={EIXO_LABELS[eixo] + ' ' + EIXO_NAMES[eixo] + ': ' + score + ' pts'} arrow>
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      bgcolor: chipColor,
                                      flexShrink: 0,
                                      cursor: 'pointer',
                                      transition: 'transform 0.15s',
                                      '&:hover': { transform: 'scale(1.3)' },
                                    }}
                                    onClick={() => navigate('/saude/' + eixo + '?tab=equipes')}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} align="center">
                          {eq.eixos_regular >= 2 ? (
                            <Chip
                              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                              label={eq.eixos_regular + ' eixos'}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: alpha('#c62828', 0.1),
                                color: '#c62828',
                                '& .MuiChip-icon': { color: '#c62828' },
                              }}
                            />
                          ) : eq.eixos_regular === 1 ? (
                            <Typography variant="caption" color="text.secondary">1 eixo</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fade>
      {municipioAtivo && dadosContexto && (
        <AnalisePredicaoCard
          dadosContexto={dadosContexto}
          municipioId={municipioAtivo.id}
          painelId="ranking"
          quadrimestre={quadrimestre}
          ano={ano}
          manualTrigger
        />
      )}
      <ChatIAButton contexto="saude" dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </Box>
  );
};

export default SaudeRanking;
